import React, {
    useEffect,
    useRef,
    useState,
} from 'react'


import {
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    EyeSlashIcon,
} from '@heroicons/react/24/outline'


import {
    BuildingOffice2Icon,
} from '@heroicons/react/24/solid'


import PulseLoader
    from 'react-spinners/PulseLoader'


import {
    useGetCoachUserPadsQuery,
} from '@/features/coachUserPads/coachUserPadsApiSlice'


import EtOrgInitiationBoard
    from '@/features/orgIntentionBoards/EtOrgInitiationBoard'



const EtCoachOrgInitiations = ({
    coachUserId,
}) => {

    // First Created - 25 Sep 2026
    // Author - Atul Pandey
    // Purpose - Provide organization level navigation for
    //           Et Coach Initiation journeys.
    //
    // Organization membership is derived from CoachUserPads.
    //
    // The selected organization is passed into:
    //
    // EtOrgInitiationBoard
    //
    // Organization names are masked by default,
    // aligned with EtCoachUserPadListCompProd.
    //
    // Updated - 25 Sep 2026
    // Added organization carousel with:
    //
    // - masked organization names
    // - show / hide organization name
    // - left / right navigation
    // - first organization auto selection


    /*
    |--------------------------------------------------------------------------
    | 01 - State
    |--------------------------------------------------------------------------
    */

    const [
        selectedOrgId,
        setSelectedOrgId,
    ] =
        useState(
            null
        )


    const [
        visibleOrganizationNames,
        setVisibleOrganizationNames,
    ] =
        useState(
            {}
        )


    const orgTabsRef =
        useRef(
            null
        )


    /*
    |--------------------------------------------------------------------------
    | 02 - Get Coach User Pads
    |--------------------------------------------------------------------------
    */

    const {

        data:
            coachUserPadsData,

        isLoading,

        isFetching,

        isError,

        error,

    } =
        useGetCoachUserPadsQuery()


    /*
    |--------------------------------------------------------------------------
    | 03 - Normalize Coach User Pads
    |--------------------------------------------------------------------------
    */

    const coachUserPads =
        coachUserPadsData
            ?.ids
            ?.map(
                id =>
                    coachUserPadsData
                        ?.entities[
                            id
                        ]
            )
            ?.filter(
                pad =>
                    String(
                        pad
                            ?.coachId
                            ?._id
                        ||
                        ''
                    ) ===
                    String(
                        coachUserId
                        ||
                        ''
                    )
            )
        ||
        []


    /*
    |--------------------------------------------------------------------------
    | 04 - Build Unique Organization List
    |--------------------------------------------------------------------------
    |
    | orgId is authoritative from:
    |
    | pad.orgId
    |
    | Organization name prefers:
    |
    | pad.orgId.organizationName
    |
    | and falls back to:
    |
    | pad.userId.userOrgId.organizationName
    |
    */

    const organizationMap =
        new Map()


    coachUserPads
        .forEach(
            pad => {

                const orgId =
                    pad
                        ?.orgId
                        ?._id
                    ||
                    pad
                        ?.orgId
                    ||
                    null


                if (
                    !orgId
                ) {

                    return

                }


                const orgName =
                    pad
                        ?.orgId
                        ?.organizationName
                    ||
                    pad
                        ?.userId
                        ?.userOrgId
                        ?.organizationName
                    ||
                    'Organization'


                if (
                    !organizationMap
                        .has(
                            String(
                                orgId
                            )
                        )
                ) {

                    organizationMap
                        .set(
                            String(
                                orgId
                            ),
                            {
                                orgId:
                                    String(
                                        orgId
                                    ),

                                orgName,
                            }
                        )

                }

            }
        )


    const organizations =
        [
            ...organizationMap
                .values(),
        ]
            .sort(
                (
                    a,
                    b
                ) =>
                    (
                        a
                            ?.orgName
                        ||
                        ''
                    )
                        .localeCompare(
                            b
                                ?.orgName
                            ||
                            ''
                        )
            )


    /*
    |--------------------------------------------------------------------------
    | 05 - Auto Select First Organization
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {

            if (
                organizations.length ===
                0
            ) {

                if (
                    selectedOrgId
                ) {

                    setSelectedOrgId(
                        null
                    )

                }


                return

            }


            const selectedOrgStillExists =
                organizations
                    .some(
                        organization =>
                            String(
                                organization
                                    ?.orgId
                            ) ===
                            String(
                                selectedOrgId
                            )
                    )


            if (
                !selectedOrgStillExists
            ) {

                setSelectedOrgId(
                    organizations[
                        0
                    ]
                        ?.orgId
                    ||
                    null
                )

            }

        },
        [
            coachUserPadsData,
            selectedOrgId,
        ]
    )


    /*
    |--------------------------------------------------------------------------
    | 06 - Organization Name Masking
    |--------------------------------------------------------------------------
    */

    const getOrgAbbreviation =
        (
            orgName =
                ''
        ) => {

            const normalizedName =
                orgName
                    .trim()


            if (
                !normalizedName
            ) {

                return 'Org***'

            }


            const nameParts =
                normalizedName
                    .split(
                        /\s+/
                    )
                    .filter(
                        Boolean
                    )


            if (
                nameParts.length ===
                1
            ) {

                const abbreviation =
                    nameParts[
                        0
                    ]
                        .slice(
                            0,
                            2
                        )
                        .replace(
                            /[^a-zA-Z0-9]/g,
                            ''
                        )


                if (
                    !abbreviation
                ) {

                    return 'Org***'

                }


                return `${
                    abbreviation
                        .charAt(
                            0
                        )
                        .toUpperCase()
                    +
                    abbreviation
                        .slice(
                            1
                        )
                        .toLowerCase()
                }***`

            }


            const abbreviation =
                nameParts
                    .map(
                        part =>
                            part
                                .charAt(
                                    0
                                )
                    )
                    .join(
                        ''
                    )
                    .toUpperCase()


            return `${
                abbreviation
                ||
                'Org'
            }***`

        }


    const getDisplayedOrgName =
        (
            orgId,
            orgName
        ) => {

            return visibleOrganizationNames[
                orgId
            ]
                ?
                orgName
                :
                getOrgAbbreviation(
                    orgName
                )

        }


    /*
    |--------------------------------------------------------------------------
    | 07 - Toggle Organization Name
    |--------------------------------------------------------------------------
    */

    const handleToggleOrganizationName =
        (
            event,
            orgId
        ) => {

            event
                .stopPropagation()


            setVisibleOrganizationNames(
                previous => ({

                    ...previous,

                    [
                        orgId
                    ]:
                        !previous[
                            orgId
                        ],

                })
            )

        }


    /*
    |--------------------------------------------------------------------------
    | 08 - Scroll Organization Tabs
    |--------------------------------------------------------------------------
    */

    const handleScrollOrgTabs =
        direction => {

            const carousel =
                orgTabsRef
                    ?.current


            if (
                !carousel
            ) {

                return

            }


            const scrollDistance =
                Math.max(
                    carousel
                        .clientWidth *
                    0.7,
                    240
                )


            carousel
                .scrollBy({

                    left:
                        direction ===
                        'left'
                            ?
                            -scrollDistance
                            :
                            scrollDistance,

                    behavior:
                        'smooth',

                })

        }


    /*
    |--------------------------------------------------------------------------
    | 09 - Loading
    |--------------------------------------------------------------------------
    */

    if (
        isLoading
        ||
        isFetching
    ) {

        return (

            <div
                className="
                    bg-white
                    m-1
                    p-5
                    rounded
                "
            >

                <PulseLoader
                    color="#ea580c"
                    size={7}
                />

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 10 - Error
    |--------------------------------------------------------------------------
    */

    if (
        isError
    ) {

        return (

            <div
                className="
                    bg-orange-50
                    m-1
                    p-4
                    rounded
                "
            >

                <p
                    className="
                        text-xs
                        font-bold
                        text-orange-700
                    "
                >
                    Unable to load coached organizations.
                </p>


                <p
                    className="
                        text-[10px]
                        text-orange-700
                        mt-1
                    "
                >
                    {
                        error
                            ?.data
                            ?.message
                        ||
                        'Coach organization information could not be loaded.'
                    }
                </p>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 11 - No Organizations
    |--------------------------------------------------------------------------
    */

    if (
        organizations.length ===
        0
    ) {

        return (

            <div
                className="
                    bg-white
                    m-1
                    p-5
                    rounded
                "
            >

                <p
                    className="
                        text-xs
                        text-gray-500
                    "
                >
                    No coached organizations are currently available.
                </p>

            </div>

        )

    }


    /*
    |--------------------------------------------------------------------------
    | 12 - Selected Organization
    |--------------------------------------------------------------------------
    */

    const selectedOrganization =
        organizations
            .find(
                organization =>
                    String(
                        organization
                            ?.orgId
                    ) ===
                    String(
                        selectedOrgId
                    )
            )
        ||
        null


    /*
    |--------------------------------------------------------------------------
    | Return
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="
                bg-gray-200
                m-1
                p-1
                rounded
                font-poppins
                mx-auto
            "
        >


            {/*
            |--------------------------------------------------------------------------
            | Organization Tabs
            |--------------------------------------------------------------------------
            */}

            <div
                className="
                    m-1
                    rounded
                    border
                    border-gray-200
                    bg-gray-50
                    p-1
                "
            >

                <div
                    className="
                        flex
                        items-center
                        gap-1
                    "
                >


                    {/*
                    |--------------------------------------------------------------------------
                    | Previous Organizations
                    |--------------------------------------------------------------------------
                    */}

                    <button
                        type="button"

                        title="Previous organizations"

                        aria-label="Scroll to previous organizations"

                        onClick={
                            () =>
                                handleScrollOrgTabs(
                                    'left'
                                )
                        }

                        className="
                            flex
                            h-8
                            w-8
                            shrink-0
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-gray-200
                            bg-white
                            text-gray-500
                            shadow-sm
                            transition
                            hover:border-orange-300
                            hover:bg-orange-50
                            hover:text-orange-600
                        "
                    >

                        <ChevronLeftIcon
                            className="
                                h-5
                                w-5
                            "
                        />

                    </button>



                    {/*
                    |--------------------------------------------------------------------------
                    | Organization Tabs
                    |--------------------------------------------------------------------------
                    */}

                    <div
                        ref={
                            orgTabsRef
                        }

                        role="tablist"

                        aria-label="Coach organizations"

                        className="
                            flex
                            min-w-0
                            flex-1
                            items-center
                            gap-1
                            overflow-x-auto
                            scroll-smooth
                            whitespace-nowrap
                            p-1
                            [scrollbar-width:none]
                            [&::-webkit-scrollbar]:hidden
                        "
                    >

                        {
                            organizations
                                .map(
                                    organization => {

                                        const orgId =
                                            organization
                                                ?.orgId


                                        const orgName =
                                            organization
                                                ?.orgName


                                        const isSelected =
                                            String(
                                                selectedOrgId
                                            ) ===
                                            String(
                                                orgId
                                            )


                                        return (

                                            <div
                                                key={
                                                    orgId
                                                }

                                                role="tab"

                                                aria-selected={
                                                    isSelected
                                                }

                                                className={`
                                                    flex
                                                    shrink-0
                                                    items-center
                                                    overflow-hidden
                                                    rounded
                                                    transition

                                                    ${
                                                        isSelected
                                                            ?
                                                            `
                                                                bg-orange-600
                                                                text-white
                                                                shadow-sm
                                                            `
                                                            :
                                                            `
                                                                border
                                                                border-gray-200
                                                                bg-white
                                                                text-gray-600
                                                                hover:border-orange-300
                                                                hover:bg-orange-50
                                                                hover:text-orange-600
                                                            `
                                                    }
                                                `}
                                            >


                                                <button
                                                    type="button"

                                                    title={
                                                        visibleOrganizationNames[
                                                            orgId
                                                        ]
                                                            ?
                                                            orgName
                                                            :
                                                            getOrgAbbreviation(
                                                                orgName
                                                            )
                                                    }

                                                    onClick={
                                                        () =>
                                                            setSelectedOrgId(
                                                                orgId
                                                            )
                                                    }

                                                    className="
                                                        px-4
                                                        py-2
                                                        text-xs
                                                        font-semibold
                                                    "
                                                >
                                                    {
                                                        getDisplayedOrgName(
                                                            orgId,
                                                            orgName
                                                        )
                                                    }
                                                </button>



                                                <button
                                                    type="button"

                                                    title={
                                                        visibleOrganizationNames[
                                                            orgId
                                                        ]
                                                            ?
                                                            'Hide organization name'
                                                            :
                                                            'Show organization name'
                                                    }

                                                    aria-label={
                                                        visibleOrganizationNames[
                                                            orgId
                                                        ]
                                                            ?
                                                            `Hide ${orgName}`
                                                            :
                                                            'Show organization name'
                                                    }

                                                    aria-pressed={
                                                        Boolean(
                                                            visibleOrganizationNames[
                                                                orgId
                                                            ]
                                                        )
                                                    }

                                                    onClick={
                                                        event =>
                                                            handleToggleOrganizationName(
                                                                event,
                                                                orgId
                                                            )
                                                    }

                                                    className={`
                                                        mr-1
                                                        flex
                                                        h-7
                                                        w-7
                                                        shrink-0
                                                        items-center
                                                        justify-center
                                                        rounded-full
                                                        transition

                                                        ${
                                                            isSelected
                                                                ?
                                                                `
                                                                    text-white
                                                                    hover:bg-orange-500
                                                                `
                                                                :
                                                                `
                                                                    text-orange-600
                                                                    hover:bg-orange-100
                                                                `
                                                        }
                                                    `}
                                                >

                                                    {
                                                        visibleOrganizationNames[
                                                            orgId
                                                        ]
                                                            ?
                                                            (
                                                                <EyeSlashIcon
                                                                    className="
                                                                        h-4
                                                                        w-4
                                                                    "
                                                                />
                                                            )
                                                            :
                                                            (
                                                                <EyeIcon
                                                                    className="
                                                                        h-4
                                                                        w-4
                                                                    "
                                                                />
                                                            )
                                                    }

                                                </button>


                                            </div>

                                        )

                                    }
                                )
                        }

                    </div>



                    {/*
                    |--------------------------------------------------------------------------
                    | Next Organizations
                    |--------------------------------------------------------------------------
                    */}

                    <button
                        type="button"

                        title="Next organizations"

                        aria-label="Scroll to next organizations"

                        onClick={
                            () =>
                                handleScrollOrgTabs(
                                    'right'
                                )
                        }

                        className="
                            flex
                            h-8
                            w-8
                            shrink-0
                            items-center
                            justify-center
                            rounded-full
                            border
                            border-gray-200
                            bg-white
                            text-gray-500
                            shadow-sm
                            transition
                            hover:border-orange-300
                            hover:bg-orange-50
                            hover:text-orange-600
                        "
                    >

                        <ChevronRightIcon
                            className="
                                h-5
                                w-5
                            "
                        />

                    </button>


                </div>

            </div>



            {/*
            |--------------------------------------------------------------------------
            | Selected Organization Header
            |--------------------------------------------------------------------------
            */}

            {
                selectedOrganization
                &&
                (
                    <div
                        className="
                            bg-gray-300
                            rounded
                            m-1
                            p-3
                        "
                    >

                        <div
                            className="
                                flex
                                justify-start
                                items-center
                                gap-2
                            "
                        >

                            <p
                                className="
                                    text-[10px]
                                    text-gray-500
                                "
                            >
                                Org:
                            </p>


                            <BuildingOffice2Icon
                                className="
                                    h-5
                                    w-5
                                    text-orange-600
                                "
                            />


                            <p
                                className="
                                    text-sm
                                    font-bold
                                    text-gray-700
                                "
                            >
                                {
                                    getDisplayedOrgName(
                                        selectedOrganization
                                            ?.orgId,

                                        selectedOrganization
                                            ?.orgName
                                    )
                                }
                            </p>


                            <button
                                type="button"

                                title={
                                    visibleOrganizationNames[
                                        selectedOrganization
                                            ?.orgId
                                    ]
                                        ?
                                        'Hide organization name'
                                        :
                                        'Show organization name'
                                }

                                onClick={
                                    event =>
                                        handleToggleOrganizationName(

                                            event,

                                            selectedOrganization
                                                ?.orgId

                                        )
                                }

                                className="
                                    flex
                                    h-6
                                    w-6
                                    items-center
                                    justify-center
                                    rounded-full
                                    text-orange-600
                                    hover:bg-white
                                "
                            >

                                {
                                    visibleOrganizationNames[
                                        selectedOrganization
                                            ?.orgId
                                    ]
                                        ?
                                        (
                                            <EyeSlashIcon
                                                className="
                                                    h-4
                                                    w-4
                                                "
                                            />
                                        )
                                        :
                                        (
                                            <EyeIcon
                                                className="
                                                    h-4
                                                    w-4
                                                "
                                            />
                                        )
                                }

                            </button>

                        </div>

                    </div>
                )
            }



            {/*
            |--------------------------------------------------------------------------
            | Organization Initiation Board
            |--------------------------------------------------------------------------
            */}

            {
                selectedOrgId
                &&
                (
                    <EtOrgInitiationBoard

                        userOrgId={
                            selectedOrgId
                        }

                    />
                )
            }


        </div>

    )

}



export default EtCoachOrgInitiations